// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AppEvent } from '@interfaces/interfaces';
import { User } from '@interfaces/user.interfaces';
import { takeWhile } from 'rxjs';
import { UserService } from '@services/user.service';
import { NavService } from '@services/nav.service';
import { scrollIntoView } from 'src/app/utils/utils';
import { legalNamePattern } from 'src/app/utils/form-utils';
import { ErrorService } from '@services/error.service';
import { PointGroup } from 'signature_pad';

@Component({
    selector: 'app-signature-form',
    templateUrl: './signature-form.component.html',
    styleUrls: ['signature-form.component.scss'],
})
export class SignatureFormComponent implements OnChanges, OnDestroy, OnInit {
    @ViewChild('legal') legalNameElem: ElementRef;
    @Input() welcomeFormButtons = false;
    @Input() hideButton = true;
    @Input() ids: string[];
    @Input() user: User;
    @Input() canEdit: boolean;
    @Input() ssoUserOnboarding = false;
    @Input() ssoUserOnboardingLegalName = '';
    @Input() hideExplanation = false;
    @Input() hideCancelButton = false;
    @Input() cancelIsReset = false;
    @Output() outEvent = new EventEmitter<AppEvent>();

    private isComponentAlive = true;

    public form: any;
    public initials: PointGroup[];
    public signature: PointGroup[];
    public formHasChanged = false;
    public formWidth: number;
    public ignoreBlobNewSignatureInProgress = false;
    public initialsBoxDimensions;
    public initialsError = false;
    public newInitials: any = null;
    public newSignature: any = null;
    public signatureBoxDimensions;
    public signatureError = false;
    public signatureForm: FormGroup;
    public errMsg: string;

    public scaledSignature: any;
    public scaledIniitials: any;

    constructor(
        private readonly navService: NavService,
        private readonly userService: UserService,
        private readonly errorService: ErrorService,
        private elementRef: ElementRef
    ) {
        this.buildForm();
    }

    @HostListener('click', ['$event'])
    onClick(event) {
        if (event.target.nodeName === 'CANVAS') {
            this.outEvent.emit({
                context: 'open-modal',
                data: {},
            });
        }
    }

    @HostListener('window:resize')
    onResize() {
        this.setCanvasWidthBasedOnFormWidth();
    }

    ngOnInit(): void {
        this.setCanvasWidthBasedOnFormWidth();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.ssoUserOnboarding && changes.user && changes.user.currentValue) {
            if (changes.user.currentValue) {
                this.userService
                    .getOtherUserSignaturesPointData(this.user)
                    .pipe(takeWhile(() => this.isComponentAlive))
                    .subscribe((pointData) => {
                        if (pointData) {
                            this.signature = JSON.parse(pointData.signature)?.signature;
                            this.initials = JSON.parse(pointData.initials)?.signature;
                        }
                    });
                this.signatureForm.setValue({
                    legalName: this.user.legalName,
                });

                this.signatureForm.updateValueAndValidity();
            }
        }

        if (this.ssoUserOnboarding) {
            if (this.ssoUserOnboarding && changes.ssoUserOnboardingLegalName) {
                this.signatureForm.setValue({
                    legalName: this.ssoUserOnboardingLegalName,
                });
                this.signatureForm.updateValueAndValidity();
            }
        }

        //if changes.canEdit is not present in changes, we don't want to set canEdit to false so falsey check doesn't cut it
        if (changes.canEdit?.currentValue === false) {
            this.signatureForm.controls['legalName'].disable();
            this.setCanvasWidthBasedOnFormWidth();
        } else if (changes.canEdit?.currentValue === true) {
            this.signatureForm.controls['legalName'].enable();
        }
    }

    ngOnDestroy(): void {
        this.isComponentAlive = false;
    }

    //The regex below is used to ensure that the user can only enter letters, numbers, diacritics, and specified chars into the Legal Name field.

    private buildForm(): void {
        this.signatureForm = new FormGroup({
            legalName: new FormControl(
                null,
                Validators.compose([Validators.required, Validators.minLength(1), Validators.maxLength(500), Validators.pattern(legalNamePattern)])
            ),
        });

        this.signatureForm
            .get('legalName')
            .valueChanges.pipe(takeWhile(() => this.isComponentAlive))
            .subscribe((value) => {
                if (!this.signatureForm.controls['legalName'].value) {
                    this.errMsg = 'Please ensure that Legal Name has a value';
                } else {
                    this.errMsg = "Please enter a valid Legal Name. Valid special characters include: (,-.').";
                }

                if (value) {
                    this.formHasChanged = true;
                }
            });
    }

    public eventHandler(event: any): void {
        switch (event.context) {
            case 'clear-signature-edit':
                this.newSignature = '';
                this.signatureError = true;
                this.setCanvasWidthBasedOnFormWidth();
                break;
            case 'clear-initials-edit':
                this.newInitials = '';
                this.initialsError = true;
                this.setCanvasWidthBasedOnFormWidth();
                break;
            case 'initials-edit':
                this.newInitials = event.data;
                this.scaledIniitials = event.data;
                this.initialsError = false;
                break;
            case 'signature-edit':
                this.newSignature = event.data;
                this.signatureError = false;
                break;
            default:
                break;
        }
    }

    public close(): void {
        // resets the form to initial values
        this.newInitials = null;
        this.newSignature = null;

        // removes the error on the forms
        this.signatureError = false;
        this.initialsError = false;
        // fills in the previous user name value
        this.signatureForm.patchValue({
            legalName: this.user.legalName,
        });

        this.outEvent.emit({
            context: 'close',
            data: {},
        });
    }

    private emitChange(changes: any): void {
        this.outEvent.emit({
            context: 'changed-values',
            data: changes,
        });
    }

    public save(): void {
        const changes = {
            signature: null,
            initials: null,
            legalName: null,
        };

        if (this.signatureForm.controls['legalName'].hasError('required')) {
            this.signatureForm.controls.legalName.markAsTouched();
            scrollIntoView('.form-control');
            this.legalNameElem.nativeElement.focus();
            return;
        }

        if (this.signatureError || this.initialsError) {
            return;
        }

        if (this.formHasChanged) {
            changes.legalName = this.signatureForm.value.legalName;
        }

        // WHEN YOU HAVE NO SIGNATURE ALREADY FILLED OUT IT IS AN EMPTY ARRAY
        if (this.signature?.length === 0) {
            if (this.newSignature === null || this.newSignature === '') {
                this.signatureError = true;
                return;
            }
        }

        if (this.signature === null || this.signature === undefined) {
            if (this.newSignature === null || this.newSignature === '') {
                this.signatureError = true;
                return;
            }
        }

        if (this.initials?.length === 0) {
            if (this.newInitials === null || this.newInitials === '') {
                this.initialsError = true;
                return;
            }
        }

        if (this.initials === null || this.initials === undefined) {
            if (this.newInitials === null || this.newInitials === '') {
                this.initialsError = true;
                return;
            }
        }

        if (this.newSignature) {
            delete this.newSignature.id;

            changes.signature = {
                ...this.newSignature,
                scaledData: undefined,
                pointData: this.newSignature.scaledData ? this.newSignature.scaledData : this.newSignature.pointData,
            };
        }

        if (this.newInitials) {
            delete this.newInitials.id;
            changes.initials = {
                ...this.newInitials,
                scaledData: undefined,
                pointData: this.newInitials.scaledData ? this.newInitials.scaledData : this.newInitials.pointData,
            };
            this.initialsError = false;
        }

        const result = !Object.values(changes).every((o) => o === null);
        const SIGNATURE_SIZE_THRESHOLD = 2000;
        const INITIALS_SIZE_THRESHOLD = 1000;

        // Verify the signature meets our signature size thresholds
        if (changes.signature.image.size <= SIGNATURE_SIZE_THRESHOLD) {
            // display modal and prevent the event from getting submitted.
            this.errorService.showError({
                title: 'Invalid Signature',
                message: 'Your signature does not meet our sizing requirements. Please redraw your signature at a larger scale.',
            });
        } else if (changes.initials.image.size <= INITIALS_SIZE_THRESHOLD) {
            // display modal and prevent the event from getting submitted.
            this.errorService.showError({
                title: 'Invalid Initials',
                message: 'Your initials do not meet our sizing requirements. Please redraw your initials at a larger scale.',
            });
        } else if (this.signatureForm.valid) {
            // Signatures are good. Send the response back over to the page
            result ? this.emitChange(changes) : this.close();
            this.ignoreBlobNewSignatureInProgress = false;
        }
    }

    private setCanvasWidthBasedOnFormWidth(): void {
        // we must maintain current dimensions if the user is creating a new signature or modifying an existing
        // canvas needs specific dimensions to work
        // adjusting dimnensions requires the canvas to be recreated and the image to be redrawn
        // redrawing the image means it will be scaled to fit the new dimensions
        // which could really mess things up if user is still working on signature
        if (this.newInitials || this.newSignature) {
            return;
        }

        this.formWidth = window.innerWidth;

        // need to subtract 5px so the box fits on the page for firefox
        this.initialsBoxDimensions = this.formWidth > 300 ? { height: 160, width: 300 } : { height: 160, width: this.formWidth - 5 };

        this.signatureBoxDimensions = this.formWidth > 600 ? { height: 260, width: 740 } : { height: 260, width: 600 };
    }

    public signOut(): void {
        if (this.cancelIsReset) {
            this.newSignature = this.signature;
            this.newInitials = this.initials;
        } else {
            this.close();
            this.navService.accountType = null;
            this.userService.logOut();
        }
    }

    public getCanvas(): HTMLCanvasElement {
        return this.elementRef.nativeElement.querySelector('#testCanvas');
    }
}
